import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import {
  Box,
  FormControl,
  Select,
  MenuItem,
  ListItemText,
  TextField,
  InputAdornment,
  Typography,
  CircularProgress,
  useTheme,
  Collapse,
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import debounce from 'lodash/debounce';
import { GridItem, Button } from '../../../../components/shared';
import { LABELS, TEST_IDS, DROPDOWN_CONFIG } from '../../constants';
import useStyles, { getSublocationMenuProps } from './style';
import { groupSublocationsByLastPosting } from '../../utils/groupSublocations';
import { formatPostingDate } from '../../utils/glPostingMethods';
import SublocationMenuItem from './SublocationMenuItem';
import GroupHeader from './GroupHeader';
import SelectedSublocationsPopover from './SelectedSublocationsPopover';

const SEARCH_DEBOUNCE_MS = 300;

const displayName = 'SublocationSelector';

const SublocationSelector = ({
  sublocations = [],
  allSublocations = [],
  selectedSublocations = [],
  searchTerm = '',
  onSublocationChange,
  onSearchChange,
  onSelectAll,
  onDeselectAll,
  isDisabled = false,
  isLoading = false,
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [localSearchTerm, setLocalSearchTerm] = useState(searchTerm);
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  const [showAllItemsInGroups, setShowAllItemsInGroups] = useState(new Set());
  const onSearchChangeRef = useRef(onSearchChange);

  const safeSublocations = useMemo(() => (Array.isArray(sublocations) ? sublocations : []), [sublocations]);
  const safeAllSublocations = useMemo(
    () => (Array.isArray(allSublocations) && allSublocations.length > 0 ? allSublocations : safeSublocations),
    [allSublocations, safeSublocations],
  );
  const safeSelectedSublocations = useMemo(
    () => (Array.isArray(selectedSublocations) ? selectedSublocations : []),
    [selectedSublocations],
  );

  // Group sublocations by last posting date range
  const groupedSublocations = useMemo(() => groupSublocationsByLastPosting(safeSublocations), [safeSublocations]);

  // Expand all groups when dropdown opens or when search is active
  useEffect(() => {
    if (isDropdownOpen || localSearchTerm) {
      const allGroupIds = groupedSublocations.map((group) => group.id);
      setExpandedGroups(new Set(allGroupIds));
    }
  }, [isDropdownOpen, groupedSublocations, localSearchTerm]);

  const selectedSublocationIdSet = useMemo(
    () => new Set(safeSelectedSublocations.map((id) => String(id))),
    [safeSelectedSublocations],
  );

  const allSublocationItems = useMemo(
    () =>
      safeAllSublocations
        .map((sub) => {
          const id = String(sub?.id ?? '').trim();
          if (!id) return null;
          const name = String(sub?.name ?? '').trim();
          return {
            id,
            value: sub?.id,
            name: name || id,
            endDate: formatPostingDate(sub?.lastPosting?.endDate) || '',
          };
        })
        .filter(Boolean),
    [safeAllSublocations],
  );

  const selectedSublocationItems = useMemo(
    () => allSublocationItems.filter((item) => selectedSublocationIdSet.has(item.id)),
    [allSublocationItems, selectedSublocationIdSet],
  );

  const unselectedSublocationItems = useMemo(
    () => allSublocationItems.filter((item) => !selectedSublocationIdSet.has(item.id)),
    [allSublocationItems, selectedSublocationIdSet],
  );

  const availableCount = safeAllSublocations.length;
  const selectedCount = safeSelectedSublocations.length;

  const selectedSublocationSummary = useMemo(
    () => ({
      title:
        availableCount > 0 && selectedCount === availableCount
          ? `All ${availableCount} selected`
          : `${selectedCount} sublocation${selectedCount === 1 ? '' : 's'}`,
      details: 'View selected and unselected sublocations',
    }),
    [availableCount, selectedCount],
  );

  useEffect(() => {
    onSearchChangeRef.current = onSearchChange;
  }, [onSearchChange]);

  // Debounced search — fires onSearchChange after 300ms of inactivity
  const debouncedSearchRef = useRef(
    debounce((value) => {
      onSearchChangeRef.current?.(value);
    }, SEARCH_DEBOUNCE_MS),
  );

  // Sync local search term when parent resets it, and cancel any queued stale search callback.
  useEffect(() => {
    debouncedSearchRef.current.cancel();
    setLocalSearchTerm(searchTerm);
  }, [searchTerm]);

  useEffect(
    () => () => {
      debouncedSearchRef.current.cancel();
    },
    [],
  );

  const handleToggle = useCallback(
    (sublocationId) => {
      const newSelection = safeSelectedSublocations.includes(sublocationId)
        ? safeSelectedSublocations.filter((id) => id !== sublocationId)
        : [...safeSelectedSublocations, sublocationId];

      onSublocationChange?.(newSelection);
    },
    [safeSelectedSublocations, onSublocationChange],
  );

  const handleSearchInput = useCallback((event) => {
    const { value } = event.target;
    setLocalSearchTerm(value);
    debouncedSearchRef.current(value);
  }, []);

  const handleSelectAllClick = useCallback(
    (event) => {
      event.stopPropagation();
      onSelectAll?.();
    },
    [onSelectAll],
  );

  const handleDeselectAllClick = useCallback(
    (event) => {
      event.stopPropagation();
      onDeselectAll?.();
    },
    [onDeselectAll],
  );

  const handleGroupToggle = useCallback((groupId) => {
    setExpandedGroups((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(groupId)) {
        newSet.delete(groupId);
      } else {
        newSet.add(groupId);
      }
      return newSet;
    });
  }, []);

  const handleGroupSelectAll = useCallback(
    (group) => {
      const groupSublocationIds = group.sublocations.map((sub) => sub.id);
      const newSelection = [...new Set([...safeSelectedSublocations, ...groupSublocationIds])];
      onSublocationChange?.(newSelection);
    },
    [safeSelectedSublocations, onSublocationChange],
  );

  const handleGroupDeselectAll = useCallback(
    (group) => {
      const groupSublocationIds = new Set(group.sublocations.map((sub) => sub.id));
      const newSelection = safeSelectedSublocations.filter((id) => !groupSublocationIds.has(id));
      onSublocationChange?.(newSelection);
    },
    [safeSelectedSublocations, onSublocationChange],
  );

  const handleToggleShowAllItems = useCallback((groupId) => {
    setShowAllItemsInGroups((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(groupId)) {
        newSet.delete(groupId);
      } else {
        newSet.add(groupId);
      }
      return newSet;
    });
  }, []);

  const renderValue = useCallback(() => {
    if (isLoading) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CircularProgress size={14} />
          <Typography variant="body2" sx={{ position: 'relative', top: 3 }}>
            {LABELS.LOADING}
          </Typography>
        </Box>
      );
    }
    if (selectedCount > 0) {
      return selectedSublocationSummary.title;
    }
    return LABELS.SELECT_SUBLOCATIONS;
  }, [isLoading, selectedCount, selectedSublocationSummary.title]);

  const menuProps = useMemo(() => getSublocationMenuProps(theme), [theme]);

  const handleDropdownClose = useCallback(() => {
    setIsDropdownOpen(false);
  }, []);

  return (
    <>
      <GridItem sx={{ width: { xs: '100%', lg: 300 }, minWidth: { lg: 300 }, flex: { lg: 'none' } }}>
        <Typography
          variant="subtitle2"
          className={classes.fieldLabel}
          color={isDisabled ? 'text.disabled' : 'text.primary'}
        >
          {LABELS.SUBLOCATIONS}
        </Typography>
        <FormControl
          fullWidth
          variant="outlined"
          size="small"
          disabled={isDisabled}
          className={isDisabled ? classes.disabledDropdown : ''}
        >
          <Select
            multiple
            value={safeSelectedSublocations}
            open={isDropdownOpen}
            onOpen={() => setIsDropdownOpen(true)}
            onClose={handleDropdownClose}
            displayEmpty
            renderValue={renderValue}
            data-testid={TEST_IDS.SUBLOCATION_SELECT}
            MenuProps={menuProps}
            disabled={isDisabled}
          >
            <Box className={classes.sublocationSearchContainer}>
              <TextField
                size="smal  l"
                fullWidth
                placeholder={LABELS.SEARCH_SUBLOCATIONS}
                value={localSearchTerm ?? ''}
                onChange={handleSearchInput}
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => e.stopPropagation()}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            <Box className={classes.sublocationButtonContainer}>
              <Button
                variant="outlined"
                size="small"
                onClick={handleSelectAllClick}
                className={classes.sublocationButton}
              >
                {LABELS.SELECT_ALL}
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={handleDeselectAllClick}
                className={classes.sublocationButton}
              >
                {LABELS.DESELECT_ALL}
              </Button>
            </Box>

            {isLoading && safeSublocations.length === 0 && (
              <Box display="flex" justifyContent="center" alignItems="center" py={4} aria-live="polite">
                <CircularProgress size={24} />
                <Typography variant="body2" ml={2}>
                  {LABELS.LOADING_SUBLOCATIONS}
                </Typography>
              </Box>
            )}

            {!isLoading && groupedSublocations.length === 0 && (
              <MenuItem disabled>
                <ListItemText primary={LABELS.NO_SUBLOCATIONS_FOUND} />
              </MenuItem>
            )}

            {!isLoading &&
              groupedSublocations.map((group) => {
                const totalItems = group.sublocations.length;
                const showAllItems = showAllItemsInGroups.has(group.id) || localSearchTerm;
                const visibleItems = showAllItems
                  ? group.sublocations
                  : group.sublocations.slice(0, DROPDOWN_CONFIG.GROUP_INITIAL_VISIBLE_ITEMS);
                const hasMore = totalItems > DROPDOWN_CONFIG.GROUP_INITIAL_VISIBLE_ITEMS;
                const remainingCount = totalItems - DROPDOWN_CONFIG.GROUP_INITIAL_VISIBLE_ITEMS;

                return (
                  <Box
                    key={group.id}
                    className={classes.groupContainer}
                    style={{
                      borderColor: group.color,
                      backgroundColor: `${group.color}03`,
                    }}
                  >
                    <GroupHeader
                      group={group}
                      isExpanded={expandedGroups.has(group.id)}
                      onToggle={handleGroupToggle}
                      onSelectAll={handleGroupSelectAll}
                      onDeselectAll={handleGroupDeselectAll}
                      classes={classes}
                    />
                    <Collapse in={expandedGroups.has(group.id)} timeout="auto">
                      {visibleItems.map((sublocation) => (
                        <SublocationMenuItem
                          key={sublocation?.id}
                          sublocation={sublocation}
                          isSelected={safeSelectedSublocations.includes(sublocation?.id)}
                          onToggle={handleToggle}
                          classes={classes}
                          groupColor={group.color}
                        />
                      ))}
                      {hasMore && !localSearchTerm && (
                        <Box className={classes.showMoreButtonContainer}>
                          <Button
                            variant="text"
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleToggleShowAllItems(group.id);
                            }}
                            className={classes.showMoreButton}
                            style={{ color: group.color }}
                          >
                            {showAllItems ? LABELS.SHOW_LESS : LABELS.SHOW_MORE(remainingCount)}
                          </Button>
                        </Box>
                      )}
                    </Collapse>
                  </Box>
                );
              })}
          </Select>
        </FormControl>
      </GridItem>

      {availableCount > 0 && (
        <SelectedSublocationsPopover
          selectedItems={selectedSublocationItems}
          unselectedItems={unselectedSublocationItems}
          summaryTitle={selectedSublocationSummary.title}
          summaryDetails={selectedSublocationSummary.details}
          onRemove={handleToggle}
          classes={classes}
        />
      )}
    </>
  );
};

SublocationSelector.displayName = displayName;

export default SublocationSelector;
